import React, { useContext, useState } from 'react';
import { Grid, Box, makeStyles, Theme, createStyles, Typography } from '@material-ui/core';
import { useTypedTranslation } from '@app/hooks';
import AppsContext, { ApplicationInterfaceTypeUnion } from './appContext';
import { PasslyButton } from '@app/components/Buttons';
import { PerrmissionsTableComponent } from '@app/components/SSOApps/addEditAppTabs/permissions/permissionsTable.component';
import { useSelector, useDispatch } from 'react-redux';
import { getModalSwitch, getLoaderBtn, getConfirmModalSwitch } from '@app/store/actions';
import { IModalData, ModalComponent, ModalActionsComponent } from '@app/components';
import { AddGroups } from '@app/pages/directoryManager/dialogs/addGroups.dialog';
import { applicationsService, appLibraryService } from '@app/services';
import { IGroupsDialogData, IWorkflowApplication } from '@app/models';
import { AxiosResponse } from 'axios';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import { genericErrorToast, isTemplate } from '@app/helpers';

interface IState {
    groupPermissionsAdded: boolean;
    selectedPermissionsIds: string[] | [];
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        buttons: {
            '& > *': {
                marginRight: theme.spacing(1)
            }
        }
    })
);

export const AppPermissions: React.SFC = () => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();
    const classes = useStyles();

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const organizationId: string = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const app = useContext(AppsContext);
    const [state, setstate] = useState<IState>({
        groupPermissionsAdded: false,
        selectedPermissionsIds: []
    });
    const [modalData, setModalData] = useState<IModalData>({
        modalSwitcher: modalSwitchState,
        headerTitle: ''
    });

    const hasGroupMembers = () => {
        return Boolean(app.Groups?.length);
    };

    const onCloseModal = () => {
        dispatch(getModalSwitch(false));
        dispatch(getLoaderBtn(false));
        dispatch(getConfirmModalSwitch(true));
    };

    const handleSucessAdd = () => {
        onCloseModal();
        dispatch(handleSetSnackbar({ show: true, message: translation('ADD_GROUP_PERMISSIONS_WARNING'), severity: 'info', dismissOnly: true }));
        refreshApp();
    };

    const handleSucessRemove = () => {
        onCloseModal();
        refreshApp();
    };

    const handleError = () => {
        onCloseModal();
    };

    const addGroups = (groupIds: string[]) => {
        applicationsService.addGroupUrl(app.Id, handleError, handleSucessAdd, groupIds, organizationId);
    };

    const removeGroups = (groupIds: string[]) => {
        if (groupIds.length) {
            applicationsService.removeGroupUrl(app.Id, handleError, handleSucessRemove, groupIds, organizationId);
        }
    };

    const handleRequestFunction = (appId: string, groups: IGroupsDialogData[]) => {
        const groupIds = groups.map(group => group.id);
        addGroups(groupIds);
    };

    // DIALOGS
    const openAddGroupMembersDialog = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('ADD_GROUPS'),
            modalSwitcher: modalSwitchState,
            form: <AddGroups dataFunction={applicationsService.getGroupsNotInAppUrl} requestFunction={handleRequestFunction} noDataLabel={'NO_MEMBER_GROUPS_TO_ADD'} noDispatch={true} organizationFilter={isTemplate(app) ? organizationId : undefined} />
        });
    };

    const closeModal = () => {
        dispatch(getModalSwitch(false));
    };

    const removeGroupsDialog = (ids: string[]) => {
        const multipleIds = ids.length > 1;
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation(multipleIds ? 'REMOVE_GROUPS_FROM_APP_HEADER' : 'REMOVE_GROUP_FROM_APP_HEADER'),
            modalSwitcher: modalSwitchState,
            form: (
                <>
                    <Box p={2}>{translation(multipleIds ? 'REMOVE_GROUPS_FROM_APP_BODY' : 'REMOVE_GROUP_FROM_APP_BODY')}</Box>
                    <ModalActionsComponent primaryBtn={translation('DELETE_BUTTON')} secondaryBtn={translation('CANCEL')} onClickPrimary={() => removeGroups(ids)} onClickSecondary={() => closeModal()} id='removeGroups' isDeleteAction={true} />
                </>
            )
        });
    };

    const handleSelectedPermissionsIds = (ids: string[]) => {
        setstate({ ...state, selectedPermissionsIds: ids });
    };

    const refreshApp = () => {
        appLibraryService.getApp(app.Id, genericErrorToast, getAppSuccess);
    };

    const getAppSuccess = (({ data }: AxiosResponse<IWorkflowApplication>) => {
        app.updateState('Groups', data.Groups);
    });

    return (
        <>
            <ModalComponent {...modalData} />
            <Grid container justifyContent='space-between' className='configpanel-row'>
                <Grid item xs={12} lg={6} className='configpanel-left'>
                    <Box p={2}>
                        <Typography variant='h6'>{translation('APP_GROUP_MEMBERS')}</Typography>
                        {hasGroupMembers() ? (
                            <>
                                <Box mb={2}>
                                    <PerrmissionsTableComponent groupPermissions={app.Groups} selectedPermissionsIds={handleSelectedPermissionsIds} onPermissionRemove={id => removeGroupsDialog([id])} />
                                </Box>
                                <Box className={classes.buttons}>
                                    <PasslyButton variant={'contained'} id={'addMembersGroupsButton'} onClick={() => removeGroupsDialog(state.selectedPermissionsIds)} disabled={!state.selectedPermissionsIds.length}>
                                        {translation('REMOVE_SELECTED')}
                                    </PasslyButton>
                                    <PasslyButton variant={'contained'} color={'primary'} id={'addMembersGroupsButton'} onClick={openAddGroupMembersDialog}>
                                        {translation('ADD_GROUPS')}
                                    </PasslyButton>
                                </Box>
                            </>
                        ) : (
                                <>
                                    <Typography variant='subtitle1'>{translation('EMPTY_APP_GROUP_MEMBERSHIP_LIST')}</Typography>
                                    <Box mt={1}>
                                    <PasslyButton color={'primary'} id={'addMembersGroupsButton'} variant={'contained'} onClick={openAddGroupMembersDialog}>
                                        {translation('ADD_GROUPS')}
                                    </PasslyButton>
                                    </Box>
                                </>
                            )}
                    </Box>
                </Grid>
                <Grid item xs={12} lg={6} className='configpanel-right'>
                    <Box p={4}>
                        <div dangerouslySetInnerHTML={{ __html: translation('APP_PERM_HELP') }}></div>
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};
